import React from 'react';

interface SkeletonProps {
    className?: string;
    animate?: boolean;
}

// Base skeleton component with shimmer effect
const Skeleton: React.FC<SkeletonProps> = ({ className = '', animate = true }) => (
    <div className={`relative overflow-hidden bg-gray-800/50 ${className}`}>
        {animate && (
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] 
        bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        )}
    </div>
);

// Media card skeleton
export const MediaCardSkeleton: React.FC<{ size?: 'small' | 'medium' | 'large' | 'full'; count?: number }> = ({
    size = 'medium',
    count = 1
}) => {
    const sizeClasses = {
        small: 'w-28 sm:w-32',
        medium: 'w-36 sm:w-40 md:w-44 lg:w-48',
        large: 'w-44 sm:w-52 md:w-60 lg:w-72',
        full: 'w-full',
    };

    return (
        <>
            {Array.from({ length: count }).map((_, index) => (
                <div
                    key={index}
                    className={`${sizeClasses[size]} animate-pulse`}
                    style={{ animationDelay: `${index * 100}ms` }}
                >
                    <Skeleton className="aspect-[2/3] rounded-xl" />
                    <div className="mt-3 space-y-2">
                        <Skeleton className="h-4 rounded-lg w-3/4" />
                        <Skeleton className="h-3 rounded-lg w-1/2" />
                    </div>
                </div>
            ))}
        </>
    );
};

// Hero slider skeleton
export const HeroSkeleton: React.FC = () => (
    <div className="relative w-full h-[60vh] sm:h-[70vh] lg:h-[85vh] bg-gray-900 animate-pulse">
        <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-transparent" />
        <div className="absolute bottom-20 left-8 sm:left-16 lg:left-24 space-y-4 max-w-2xl">
            <Skeleton className="h-8 sm:h-12 lg:h-16 w-96 rounded-lg" />
            <Skeleton className="h-4 w-80 rounded-lg" />
            <Skeleton className="h-4 w-64 rounded-lg" />
            <div className="flex gap-4 mt-6">
                <Skeleton className="h-12 w-32 rounded-full" />
                <Skeleton className="h-12 w-40 rounded-full" />
            </div>
        </div>
    </div>
);

// Channel card skeleton
export const ChannelCardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => (
    <>
        {Array.from({ length: count }).map((_, index) => (
            <div
                key={index}
                className="animate-pulse"
                style={{ animationDelay: `${index * 50}ms` }}
            >
                <Skeleton className="aspect-video rounded-2xl" />
            </div>
        ))}
    </>
);

// Video card skeleton (for YouTube)
export const VideoCardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => (
    <>
        {Array.from({ length: count }).map((_, index) => (
            <div
                key={index}
                className="animate-pulse"
                style={{ animationDelay: `${index * 75}ms` }}
            >
                <Skeleton className="aspect-video rounded-xl" />
                <div className="mt-3 space-y-2">
                    <Skeleton className="h-5 rounded-lg w-full" />
                    <Skeleton className="h-4 rounded-lg w-2/3" />
                </div>
            </div>
        ))}
    </>
);

// Cast grid skeleton
export const CastSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="flex flex-col items-center animate-pulse">
                <Skeleton className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" />
                <Skeleton className="h-3 w-20 rounded mt-2" />
                <Skeleton className="h-2 w-16 rounded mt-1" />
            </div>
        ))}
    </div>
);

// Episode grid skeleton
export const EpisodeSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {Array.from({ length: count }).map((_, index) => (
            <Skeleton
                key={index}
                className="h-16 sm:h-20 rounded-xl"
                animate
            />
        ))}
    </div>
);

// Full page loading
export const PageLoader: React.FC = () => (
    <div className="fixed inset-0 bg-gray-950 flex items-center justify-center z-50">
        <div className="relative">
            {/* Animated rings */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-amber-500/20 
        animate-ping absolute inset-0" />
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-transparent 
        border-t-amber-500 animate-spin" />

            {/* Center dot */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 bg-amber-500 rounded-full animate-pulse" />
            </div>
        </div>

        {/* Loading text */}
        <div className="absolute mt-36 text-center">
            <p className="text-amber-500 font-semibold text-lg animate-pulse">Loading...</p>
        </div>
    </div>
);

// Section loading
export const SectionLoader: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
    <div className="flex flex-col items-center justify-center py-20">
        <div className="relative w-16 h-16">
            <div className="w-16 h-16 rounded-full border-4 border-amber-500/20 
        animate-ping absolute inset-0" />
            <div className="w-16 h-16 rounded-full border-4 border-transparent 
        border-t-amber-500 animate-spin" />
        </div>
        <p className="mt-6 text-gray-400 font-medium">{message}</p>
    </div>
);

export default Skeleton;
